import type { HttpInterceptorFn } from '@angular/common/http';

import { environment } from '~environments/environment.development';
import { safeStorageGet, safeStorageSet } from '~utils/safe-storage';

const DEVICE_ID_STORAGE_KEY = 'x-device-id';

/**
 * In-memory fallback so the device ID remains stable within a session
 * even when localStorage is unavailable (privacy mode, restricted webviews).
 */
let inMemoryDeviceId: string | null = null;

/**
 * Generates a unique device ID and persists it in localStorage.
 * Falls back to an in-memory value when storage is blocked — never throws.
 */
const getDeviceId = (): string => {
  const stored = safeStorageGet(DEVICE_ID_STORAGE_KEY);

  if (stored) {
    inMemoryDeviceId = stored;

    return stored;
  }

  if (inMemoryDeviceId) {
    return inMemoryDeviceId;
  }

  const newId = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;

  safeStorageSet(DEVICE_ID_STORAGE_KEY, newId);
  inMemoryDeviceId = newId;

  return newId;
};

/**
 * Intercepts HTTP requests to add required headers for API calls
 * Adds X-App-Name and X-Device-Id headers to requests matching the API base URL
 * Only applies to QA and PROD environments (skips development)
 */
export const apiHeadersInterceptor: HttpInterceptorFn = (req, next) => {
  // Skip adding headers in development environment
  if (environment.isDevelopment) {
    return next(req);
  }

  if (req.url.startsWith(environment.apiBaseUrl)) {
    const modifiedReq = req.clone({
      setHeaders: {
        'X-App-Name': 'StorMap-Web',
        'X-Device-Id': getDeviceId(),
      },
    });

    return next(modifiedReq);
  }

  return next(req);
};
